import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import Breadcrumb from "../../components/shared/Breadcrumb";
import {
  createCoreTrabajadorContacto,
  deactivateCoreTrabajadorContacto,
  getCoreTrabajadoresCatalogos,
  listCoreTrabajadoresContactos,
  updateCoreTrabajadorContacto,
} from "../../platform/apiClient";
import { readSessionRole } from "../../platform/sessionContext";

const PAGE_SIZES = [10, 50, 100, 500];
const CONTACT_TYPES = [
  { value: "directo", label: "Directo (principal)" },
  { value: "secundario", label: "Secundario" },
  { value: "general", label: "General" },
];

function emptyForm() {
  return { nombre: "", rut: "", email: "", telefono_movil: "", empresa_id: "", empresa_nombre: "", empresa_rut: "", cargo_id: "", cargo_nombre: "", area_id: "", area_nombre: "", tipo_contacto: "general", vigencia_desde: "" };
}

function formFromContact(contact) {
  return {
    nombre: contact?.nombre || "",
    rut: contact?.rut || "",
    email: contact?.email || "",
    telefono_movil: contact?.telefono_movil || "",
    empresa_id: contact?.empresa?.id ? String(contact.empresa.id) : "",
    empresa_nombre: contact?.empresa?.fantasia ? `${contact.empresa.nombre} · ${contact.empresa.fantasia}` : contact?.empresa?.nombre || "",
    empresa_rut: contact?.empresa?.rut || "",
    cargo_id: contact?.cargo?.id ? String(contact.cargo.id) : "",
    cargo_nombre: contact?.cargo?.nombre || "",
    area_id: contact?.area?.id ? String(contact.area.id) : "",
    area_nombre: contact?.area?.nombre || "",
    tipo_contacto: contact?.tipo_contacto || "general",
    vigencia_desde: contact?.vigencia_desde ? String(contact.vigencia_desde).slice(0, 10) : "",
  };
}

function exportRows(contacts) {
  return contacts.map((contact) => ({
    Nombre: contact.nombre,
    RUT: contact.rut,
    Email: contact.email,
    Teléfono: contact.telefono_movil,
    Empresa: contact.empresa?.nombre,
    Cargo: contact.cargo?.nombre || "",
    Área: contact.area?.nombre || "",
    "Tipo de contacto": contact.tipo_contacto,
    "Condición": contact.es_trabajador ? "Trabajador" : "Contacto sin cargo",
  }));
}

function downloadBlob(content, filename, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function escapeCsv(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(contacts) {
  const rows = exportRows(contacts);
  const columns = Object.keys(rows[0] || { Nombre: "", RUT: "", Empresa: "" });
  const csv = [columns.join(","), ...rows.map((row) => columns.map((column) => escapeCsv(row[column])).join(","))].join("\n");
  downloadBlob(`\ufeff${csv}`, "contactos.csv", "text/csv;charset=utf-8;");
}

function downloadExcel(contacts) {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows(contacts)), "Contactos");
  XLSX.writeFile(workbook, "contactos.xlsx");
}

function downloadPdf(contacts) {
  const rows = exportRows(contacts);
  const lines = [
    `Directorio de contactos - ${new Date().toLocaleDateString("es-CL")}`,
    "Nombre | Empresa | Cargo | Tipo",
    ...rows.map((row) => `${row.Nombre} | ${row.Empresa} | ${row.Cargo} | ${row["Tipo de contacto"]}`),
  ];
  const chunks = [];
  for (let index = 0; index < lines.length; index += 48) chunks.push(lines.slice(index, index + 48));
  const firstPageObject = 3;
  const fontObject = firstPageObject + chunks.length * 2;
  const objects = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[2] = `<< /Type /Pages /Kids [${chunks.map((_, index) => `${firstPageObject + index * 2} 0 R`).join(" ")}] /Count ${chunks.length} >>`;
  chunks.forEach((chunk, index) => {
    const pageObject = firstPageObject + index * 2;
    const contentObject = pageObject + 1;
    const safe = (value) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "?").replace(/[^\x20-\x7E]/g, "?").replace(/[\\()]/g, "\\$&").slice(0, 125);
    const content = ["BT", "/F1 9 Tf", "40 760 Td", ...chunk.map((line, lineIndex) => `${lineIndex ? "0 -14 Td " : ""}(${safe(line)}) Tj`), "ET"].join("\n");
    objects[pageObject] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObject} 0 R >> >> /Contents ${contentObject} 0 R >>`;
    objects[contentObject] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });
  objects[fontObject] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let index = 1; index <= fontObject; index += 1) { offsets[index] = pdf.length; pdf += `${index} 0 obj\n${objects[index]}\nendobj\n`; }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${fontObject + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= fontObject; index += 1) pdf += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  pdf += `trailer\n<< /Size ${fontObject + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  downloadBlob(pdf, "contactos.pdf", "application/pdf");
}

function typeLabel(type) {
  return CONTACT_TYPES.find((item) => item.value === type)?.label || "General";
}

export default function ContactosPage() {
  const [contacts, setContacts] = useState([]);
  const [catalogs, setCatalogs] = useState({ empresas: [], areas: [], cargos: [] });
  const [search, setSearch] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [tipoContacto, setTipoContacto] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modal, setModal] = useState(null);
  const canManage = ["admin", "superadmin"].includes(readSessionRole());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listCoreTrabajadoresContactos({ page, pageSize, search, companyId, tipoContacto });
      setContacts(response.contactos || []);
      setPagination(response.pagination || { total: 0 });
    } catch (reason) {
      setError(reason.message || "No se pudieron cargar los contactos.");
      setContacts([]);
    } finally { setLoading(false); }
  }, [page, pageSize, search, companyId, tipoContacto]);

  useEffect(() => { getCoreTrabajadoresCatalogos().then(setCatalogs).catch((reason) => setError(reason.message || "No se pudieron cargar los catálogos.")); }, []);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pageSize));
  const companyOptions = useMemo(() => catalogs.empresas || [], [catalogs.empresas]);

  function changeFilter(setter, value) { setter(value); setPage(1); }

  async function saveContact(form) {
    setBusy(true); setError(""); setMessage("");
    const payload = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value === "" ? null : value]));
    try {
      if (modal.mode === "edit") await updateCoreTrabajadorContacto(modal.contact.id, payload);
      else await createCoreTrabajadorContacto(payload);
      setModal(null);
      setMessage(modal.mode === "edit" ? "Contacto actualizado correctamente." : "Contacto creado correctamente.");
      getCoreTrabajadoresCatalogos().then(setCatalogs).catch(() => {});
      await load();
    } catch (reason) { setError(reason.message || "No se pudo guardar el contacto."); }
    finally { setBusy(false); }
  }

  async function deactivate(contact) {
    if (!window.confirm(`¿Desactivar la relación de ${contact.nombre} con ${contact.empresa?.nombre || "la empresa"}?`)) return;
    setBusy(true); setError(""); setMessage("");
    try { await deactivateCoreTrabajadorContacto(contact.id); setMessage("Contacto desactivado correctamente."); await load(); }
    catch (reason) { setError(reason.message || "No se pudo desactivar el contacto."); }
    finally { setBusy(false); }
  }

  async function exportAll(format) {
    setBusy(true); setError("");
    try {
      const all = [];
      let nextPage = 1;
      let total = 0;
      do {
        const response = await listCoreTrabajadoresContactos({ page: nextPage, pageSize: 500, search, companyId, tipoContacto });
        all.push(...(response.contactos || [])); total = Number(response.pagination?.total || all.length); nextPage += 1;
      } while (all.length < total && nextPage < 100);
      if (format === "csv") downloadCsv(all);
      if (format === "excel") downloadExcel(all);
      if (format === "pdf") downloadPdf(all);
    } catch (reason) { setError(reason.message || "No se pudo generar la exportación."); }
    finally { setBusy(false); }
  }

  return (
    <div className="dashboard-main-body">
      <Breadcrumb title="Contactos" text="Directorio / Empresas / Contactos" />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-24">
        <div><h6 className="fw-semibold mb-4">Contactos de empresas</h6><p className="text-secondary-light mb-0">Personas relacionadas con empresas, sean trabajadoras o contactos sin cargo.</p></div>
        {canManage && <button type="button" className="btn btn-primary d-flex align-items-center gap-2" onClick={() => setModal({ mode: "create" })}><Icon icon="solar:add-circle-linear" />Nueva persona</button>}
      </div>
      {error && <div className="alert alert-danger py-12">{error}</div>}
      {message && <div className="alert alert-success py-12">{message}</div>}
      <div className="card basic-data-table">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
            <div className="input-group" style={{ maxWidth: 360 }}><span className="input-group-text bg-transparent"><Icon icon="ion:search-outline" /></span><input className="form-control" value={search} onChange={(event) => changeFilter(setSearch, event.target.value)} placeholder="Buscar nombre, RUT, empresa o teléfono" /></div>
            <select className="form-select" style={{ maxWidth: 240 }} value={companyId} onChange={(event) => changeFilter(setCompanyId, event.target.value)}><option value="">Todas las empresas</option>{companyOptions.map((company) => <option key={company.id} value={company.id}>{company.fantasia || company.nombre}</option>)}</select>
            <select className="form-select" style={{ maxWidth: 190 }} value={tipoContacto} onChange={(event) => changeFilter(setTipoContacto, event.target.value)}><option value="">Todos los tipos</option>{CONTACT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
          </div>
          <div className="d-flex align-items-center gap-2"><span className="text-sm text-secondary-light">Mostrar</span><select className="form-select form-select-sm w-auto" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}</select><button type="button" className="btn btn-sm btn-outline-success" title="Exportar Excel" onClick={() => exportAll("excel")} disabled={busy}><Icon icon="solar:file-download-linear" /></button><button type="button" className="btn btn-sm btn-outline-secondary" title="Exportar CSV" onClick={() => exportAll("csv")} disabled={busy}><Icon icon="solar:document-text-linear" /></button><button type="button" className="btn btn-sm btn-outline-danger" title="Exportar PDF" onClick={() => exportAll("pdf")} disabled={busy}><Icon icon="solar:file-download-linear" /></button></div>
        </div>
        <div className="table-responsive"><table className="table bordered-table mb-0 align-middle"><thead><tr><th>Contacto</th><th>Empresa</th><th>Cargo / área</th><th>Clasificación</th><th>Condición</th><th className="text-end">Acciones</th></tr></thead><tbody>
          {loading && <tr><td colSpan="6" className="text-center py-32">Cargando contactos...</td></tr>}
          {!loading && contacts.map((contact) => <tr key={contact.id}>
            <td><div className="d-flex align-items-center gap-2">{contact.tipo_contacto === "directo" && <Icon icon="solar:star-bold" className="text-warning" title="Contacto principal" />}<div><div className="fw-semibold">{contact.nombre || "—"}</div><div className="text-sm text-secondary-light">{contact.email || contact.telefono_movil || "Sin datos de contacto"}</div></div></div></td>
            <td><div>{contact.empresa?.nombre || "—"}</div>{contact.empresa?.fantasia && <div className="text-sm text-secondary-light">{contact.empresa.fantasia}</div>}</td>
            <td>{contact.cargo?.nombre || "Sin cargo"}{contact.area?.nombre && <div className="text-sm text-secondary-light">{contact.area.nombre}</div>}</td>
            <td><span className={`badge ${contact.tipo_contacto === "directo" ? "bg-warning-50 text-warning-700" : contact.tipo_contacto === "secundario" ? "bg-info-50 text-info-700" : "bg-secondary-100 text-secondary-700"}`}>{typeLabel(contact.tipo_contacto)}</span></td>
            <td><span className={`badge ${contact.es_trabajador ? "bg-success-50 text-success-600" : "bg-light text-secondary-light"}`}>{contact.es_trabajador ? "Trabajador" : "Contacto sin cargo"}</span></td>
            <td className="text-end"><button type="button" className="btn btn-sm btn-outline-secondary me-2" title="Ver" onClick={() => setModal({ mode: "view", contact })}><Icon icon="iconamoon:eye-light" /></button>{canManage && <button type="button" className="btn btn-sm btn-outline-primary me-2" title="Editar" onClick={() => setModal({ mode: "edit", contact })}><Icon icon="lucide:edit" /></button>}{canManage && <button type="button" className="btn btn-sm btn-outline-danger" title="Desactivar" onClick={() => deactivate(contact)} disabled={busy}><Icon icon="mingcute:delete-2-line" /></button>}</td>
          </tr>)}
          {!loading && contacts.length === 0 && <tr><td colSpan="6" className="text-center text-secondary-light py-32">No hay contactos para mostrar.</td></tr>}
        </tbody></table></div>
        <div className="d-flex align-items-center justify-content-between mt-16 px-20 pb-20"><button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</button><span className="text-sm text-secondary-light">Página {page} de {totalPages} · {pagination.total || 0} registros</span><button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>Siguiente</button></div>
      </div>
      {modal && <ContactModal mode={modal.mode} contact={modal.contact} catalogs={catalogs} busy={busy} onClose={() => setModal(null)} onSave={saveContact} />}
    </div>
  );
}

function ContactModal({ mode, contact, catalogs, busy, onClose, onSave }) {
  const readOnly = mode === "view";
  const [form, setForm] = useState(() => mode === "create" ? emptyForm() : formFromContact(contact));
  const [formError, setFormError] = useState("");
  const title = mode === "create" ? "Nueva persona relacionada" : mode === "edit" ? "Editar contacto" : "Detalle de contacto";
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value, ...(field === "cargo_id" && !value ? { area_id: "", tipo_contacto: "general" } : {}) }));
  function submit(event) {
    event.preventDefault();
    if (!form.nombre.trim()) { setFormError("El nombre completo es obligatorio."); return; }
    if (!form.empresa_id && form.empresa_nombre.trim() && !form.empresa_rut.trim()) { setFormError("La empresa no existe en el catálogo. Informa su RUT para registrarla o deja el campo empresa vacío."); return; }
    if (!form.empresa_id && (form.cargo_id || form.area_id || form.cargo_nombre.trim() || form.area_nombre.trim())) { setFormError("Una persona sin empresa debe ser un contacto general y no puede tener cargo ni área."); return; }
    if (form.cargo_id && !form.area_id) { setFormError("El área es obligatoria cuando informas un cargo."); return; }
    if (!form.cargo_id && form.area_id) { setFormError("No puedes informar área sin cargo."); return; }
    if (!form.cargo_id && form.tipo_contacto !== "general") { setFormError("Sin cargo, el contacto debe ser general."); return; }
    setFormError(""); onSave(form);
  }
  const companyIsNew = !form.empresa_id && form.empresa_nombre.trim();
  return <div className="modal d-block" style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }} role="dialog" aria-modal="true" aria-label={title} onClick={onClose}><div className="modal-dialog modal-dialog-centered modal-lg" onClick={(event) => event.stopPropagation()}><div className="modal-content"><div className="modal-header"><h6 className="modal-title">{title}</h6><button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} /></div><form onSubmit={submit}><div className="modal-body"><p className="text-secondary-light mb-20">La empresa es opcional. Sin empresa, la persona se registra como contacto general. Puedes buscar un cargo o área existente; si escribes uno nuevo, se creará automáticamente.</p>{formError && <div className="alert alert-danger py-10">{formError}</div>}<div className="row gy-3"><Field label="Nombre completo" value={form.nombre} onChange={(value) => update("nombre", value)} required disabled={readOnly} /><Field label="RUT (opcional)" value={form.rut} onChange={(value) => update("rut", value)} disabled={readOnly} placeholder="Ej.: 12.345.678-9" /><Field label="Email" type="email" value={form.email} onChange={(value) => update("email", value)} disabled={readOnly} /><Field label="Teléfono móvil" value={form.telefono_movil} onChange={(value) => update("telefono_movil", value)} disabled={readOnly} /><div className="col-md-6"><CatalogCombobox label="Empresa (opcional)" value={form.empresa_nombre} options={catalogs.empresas} optionLabel={(item) => item.fantasia ? `${item.nombre} · ${item.fantasia}` : item.nombre} disabled={readOnly} onChange={({ id, name }) => setForm((current) => ({ ...current, empresa_id: id, empresa_nombre: name, ...(id ? { empresa_rut: "" } : {}) }))} /></div>{companyIsNew && <Field label="RUT de la nueva empresa" value={form.empresa_rut} onChange={(value) => update("empresa_rut", value)} disabled={readOnly} required placeholder="Ej.: 76.123.456-7" />}<SelectField label="Tipo de contacto" value={form.tipo_contacto} onChange={(value) => update("tipo_contacto", value)} options={CONTACT_TYPES} optionValue="value" optionLabel={(item) => item.label} disabled={readOnly} /><div className="col-md-6"><CatalogCombobox label="Cargo (opcional)" value={form.cargo_nombre} options={catalogs.cargos} disabled={readOnly} onChange={({ id, name }) => setForm((current) => ({ ...current, cargo_id: id, cargo_nombre: name, ...(id ? {} : { area_id: "", area_nombre: "" }) }))} /></div><div className="col-md-6"><CatalogCombobox label="Área (obligatoria con cargo)" value={form.area_nombre} options={catalogs.areas} disabled={readOnly || !form.cargo_nombre.trim()} onChange={({ id, name }) => setForm((current) => ({ ...current, area_id: id, area_nombre: name }))} /></div></div></div><div className="modal-footer"><button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>{!readOnly && <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Guardando..." : "Guardar"}</button>}</div></form></div></div></div>;
}

function Field({ label, value, onChange, type = "text", required = false, disabled = false, placeholder = "" }) {
  return <div className="col-md-6"><label className="form-label">{label}{required && " *"}</label><input className="form-control" type={type} value={value || ""} required={required} disabled={disabled} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} /></div>;
}

function SelectField({ label, value, onChange, options = [], optionValue = "id", optionLabel = (item) => item.nombre, required = false, disabled = false }) {
  return <div className="col-md-6"><label className="form-label">{label}{required && " *"}</label><select className="form-select" value={value || ""} required={required} disabled={disabled} onChange={(event) => onChange(event.target.value)}><option value="">Seleccionar...</option>{options.map((item) => <option key={item[optionValue]} value={item[optionValue]}>{optionLabel(item)}</option>)}</select></div>;
}

function searchKey(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
}

function CatalogCombobox({ label, value, options = [], optionLabel = (item) => item.nombre, onChange, disabled = false, required = false }) {
  const listId = `catalog-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
  function handleChange(event) {
    const name = event.target.value;
    const selected = options.find((item) => searchKey(optionLabel(item)) === searchKey(name) || searchKey(item.nombre) === searchKey(name));
    onChange({ id: selected ? String(selected.id) : "", name });
  }
  return <><label className="form-label">{label}{required && " *"}</label><input className="form-control" list={listId} value={value || ""} required={required} disabled={disabled} onChange={handleChange} placeholder="Buscar o escribir para crear" />{!disabled && <datalist id={listId}>{options.map((item) => <option key={item.id} value={optionLabel(item)} />)}</datalist>}</>;
}
